// Super_admin-only top-level page for managing per-publisher Waymark
// agreements (CTV-643). Lives at /admin/waymark-credentials.
//
// Write-only by design: the signing keys are never returned by the API, so the
// table shows only whether a key is set. Leaving a key field blank on save
// keeps the current one; typing a new value rotates it.
//
// Auth mirrors PublisherOnboardPage:
//   • signed out                 → redirect to /admin (single sign-in surface)
//   • signed in, not super_admin  → redirect to /admin
//   • signed in, super_admin      → render

import React from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { ArrowLeft, KeyRound, Loader2, ShieldCheck, ShieldX } from 'lucide-react';
import { apiClient, ApiError } from '../../lib/apiClient';
import { useAuth } from '../../components/self-service/auth/useAuth';

interface WaymarkOrg {
  id: string;
  name: string;
  waymarkPartnerId: string | null;
  waymarkEnvironment: string | null;
  hasSecret: boolean;
  hasWebhookSecret: boolean;
  pinnedDraftCount: number;
  // The subset of pinnedDraftCount that is mid-generation with no finished
  // creative. Clearing/repointing the partner leaves those buyers unable to
  // finish their commercial at all, so it's called out separately.
  strandedDraftCount: number;
}

interface SetPayload {
  partnerId: string | null;
  environment?: 'prod' | 'demo';
  secret?: string;
  webhookSecret?: string | null;
  confirmDraftBreakage?: boolean;
}

const WaymarkCredentialsPage: React.FC = () => {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();

  const [orgs, setOrgs] = React.useState<WaymarkOrg[] | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [editing, setEditing] = React.useState<WaymarkOrg | null>(null);

  const load = React.useCallback(async () => {
    setLoadError(null);
    try {
      setOrgs(await apiClient.get<WaymarkOrg[]>('/admin/waymark/organizations'));
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load organizations');
    }
  }, []);

  React.useEffect(() => {
    if (isAuthenticated && user?.rawRole === 'super_admin') void load();
  }, [isAuthenticated, user?.rawRole, load]);

  if (!isAuthenticated || !user) {
    return <Navigate to="/admin" state={{ from: location.pathname + location.search }} replace />;
  }
  if (user.rawRole !== 'super_admin') {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-8 py-10 md:py-14">
        <Link
          to="/admin"
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> Back to admin
        </Link>
        <div className="mb-8">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-900 text-white text-[10px] font-semibold uppercase tracking-[0.14em] mb-4">
            <KeyRound className="w-3 h-3" />
            Waymark credentials
          </div>
          <h1 className="text-[28px] md:text-[36px] font-semibold tracking-tight text-slate-900 leading-tight">
            Per-publisher Waymark agreements
          </h1>
          <p className="mt-3 text-[15px] text-slate-500 leading-relaxed max-w-2xl">
            Publishers with their own Waymark agreement sign with their own partner. Everyone
            else falls back to CTVBuyer's platform credentials. Signing keys are write-only:
            they are never shown here, and leaving a key field blank keeps the current one.
          </p>
        </div>

        {loadError && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {loadError}
          </div>
        )}

        {orgs === null && !loadError && (
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading organizations…
          </div>
        )}

        {orgs !== null && orgs.length === 0 && (
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-6 text-sm text-slate-500">
            No organizations found.
          </div>
        )}

        {orgs !== null && orgs.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Organization</th>
                  <th className="px-4 py-3 font-medium">Partner ID</th>
                  <th className="px-4 py-3 font-medium">Env</th>
                  <th className="px-4 py-3 font-medium">Signing key</th>
                  <th className="px-4 py-3 font-medium">Webhook key</th>
                  <th className="px-4 py-3 font-medium">Commercials</th>
                  <th className="px-4 py-3 font-medium sr-only">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {orgs.map((o) => (
                  <tr key={o.id}>
                    <td className="px-4 py-3 font-medium text-slate-900">{o.name}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {o.waymarkPartnerId ?? (
                        <span className="text-slate-400">Platform fallback</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{o.waymarkEnvironment ?? '—'}</td>
                    <td className="px-4 py-3">
                      <KeyBadge present={o.hasSecret} />
                    </td>
                    <td className="px-4 py-3">
                      <KeyBadge present={o.hasWebhookSecret} />
                    </td>
                    <td className="px-4 py-3">
                      <CommercialsCell total={o.pinnedDraftCount} unfinished={o.strandedDraftCount} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => setEditing(o)}
                        className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <WaymarkEditor
          org={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}
    </div>
  );
};

// Buyer commercials tied to this org's Waymark partner. The `unfinished` subset
// (still being generated, no final video yet) is the at-risk group: those
// buyers can't complete their campaign at all if the agreement changes.
const CommercialsCell: React.FC<{ total: number; unfinished: number }> = ({ total, unfinished }) => {
  if (total === 0) return <span className="text-slate-400">—</span>;
  return (
    <span className="text-xs text-slate-600">
      {total} commercial{total === 1 ? '' : 's'}
      {unfinished > 0 && (
        <span className="block text-rose-600 font-medium">
          {unfinished} still unfinished
        </span>
      )}
    </span>
  );
};

const KeyBadge: React.FC<{ present: boolean }> = ({ present }) =>
  present ? (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
      <ShieldCheck className="w-3.5 h-3.5" /> Set
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-400">
      <ShieldX className="w-3.5 h-3.5" /> None
    </span>
  );

const WaymarkEditor: React.FC<{
  org: WaymarkOrg;
  onClose: () => void;
  onSaved: () => void;
}> = ({ org, onClose, onSaved }) => {
  const [partnerId, setPartnerId] = React.useState(org.waymarkPartnerId ?? '');
  const [environment, setEnvironment] = React.useState<'prod' | 'demo'>(
    (org.waymarkEnvironment as 'prod' | 'demo') ?? 'demo',
  );
  const [secret, setSecret] = React.useState('');
  const [webhookSecret, setWebhookSecret] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  const submit = React.useCallback(
    async (payload: SetPayload) => {
      setSaving(true);
      setError(null);
      try {
        await apiClient.put(`/admin/waymark/organizations/${org.id}`, payload);
        onSaved();
      } catch (e) {
        if (e instanceof ApiError && e.status === 409) {
          const body = e.body as
            | { code?: string; pinnedDraftCount?: number; strandedDraftCount?: number }
            | undefined;
          if (body?.code === 'waymark_pinned_drafts') {
            const n = body.pinnedDraftCount ?? 0;
            const unfinished = body.strandedDraftCount ?? 0;
            const unfinishedLine =
              unfinished > 0
                ? `\n\n${unfinished} ${unfinished === 1 ? 'is' : 'are'} still unfinished — ` +
                  `${unfinished === 1 ? 'that buyer' : 'those buyers'} will NOT be able to finish ` +
                  `${unfinished === 1 ? 'their commercial' : 'their commercials'}.`
                : '';
            const ok = window.confirm(
              `${n} buyer commercial${n === 1 ? '' : 's'} ${n === 1 ? 'was' : 'were'} created ` +
                `under the current Waymark partner and can no longer be reopened for editing.` +
                `${unfinishedLine}\n\nProceed anyway?`,
            );
            if (ok) {
              await submit({ ...payload, confirmDraftBreakage: true });
              return;
            }
            setSaving(false);
            return;
          }
        }
        setError(e instanceof Error ? e.message : 'Save failed');
        setSaving(false);
      }
    },
    [org.id, onSaved],
  );

  const onSave = () => {
    const trimmed = partnerId.trim();
    if (!trimmed) {
      setError('Partner ID is required. Use "Clear agreement" to remove it.');
      return;
    }
    if (!org.hasSecret && !secret.trim()) {
      setError('A signing key is required for a new agreement.');
      return;
    }
    const payload: SetPayload = { partnerId: trimmed, environment };
    if (secret.trim()) payload.secret = secret.trim();
    if (webhookSecret.trim()) payload.webhookSecret = webhookSecret.trim();
    void submit(payload);
  };

  const onClear = () => {
    if (!window.confirm(`Remove ${org.name}'s Waymark agreement? They will use the platform credentials.`)) {
      return;
    }
    void submit({ partnerId: null });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-slate-900">{org.name}</h2>
        <p className="mt-1 text-sm text-slate-500">Waymark agreement</p>

        {error && (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mt-5 space-y-4">
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Partner ID</span>
            <input
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              placeholder="Waymark partner ID"
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-slate-700">Environment</span>
            <select
              value={environment}
              onChange={(e) => setEnvironment(e.target.value as 'prod' | 'demo')}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="demo">demo</option>
              <option value="prod">prod</option>
            </select>
          </label>

          <label className="block">
            <span className="text-sm font-medium text-slate-700">Signing key</span>
            <input
              type="password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              autoComplete="new-password"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              placeholder={org.hasSecret ? 'Leave blank to keep current key' : 'Required for a new agreement'}
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-slate-700">Webhook key (optional)</span>
            <input
              type="password"
              value={webhookSecret}
              onChange={(e) => setWebhookSecret(e.target.value)}
              autoComplete="new-password"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              placeholder={org.hasWebhookSecret ? 'Leave blank to keep current key' : 'Optional'}
            />
          </label>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <button
            type="button"
            onClick={onClear}
            disabled={saving || !org.waymarkPartnerId}
            className="text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-40"
          >
            Clear agreement
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaymarkCredentialsPage;
